import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Carrusel manual sobre scroll-snap nativo: el gesto horizontal lo resuelve el
 * navegador (no bloquea el scroll vertical) y el índice activo se deriva de la
 * posición de scroll. El track debe tener `position: relative`.
 */
export function useCarousel(count: number) {
  const trackRef = useRef<HTMLDivElement>(null)
  const [index, setIndex] = useState(0)

  useEffect(() => {
    const el = trackRef.current
    if (!el) return
    let frame = 0

    const measure = () => {
      frame = 0
      const max = el.scrollWidth - el.clientWidth
      if (max <= 1) {
        setIndex(0)
        return
      }
      if (el.scrollLeft >= max - 2) {
        setIndex(count - 1)
        return
      }
      const pl = parseFloat(getComputedStyle(el).paddingLeft) || 0
      const start = el.getBoundingClientRect().left + pl
      let best = 0
      let bestDist = Infinity
      Array.from(el.children).forEach((child, i) => {
        const d = Math.abs((child as HTMLElement).getBoundingClientRect().left - start)
        if (d < bestDist) {
          bestDist = d
          best = i
        }
      })
      setIndex(best)
    }

    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(measure)
    }

    el.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      if (frame) window.cancelAnimationFrame(frame)
      el.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [count])

  const goTo = useCallback(
    (i: number) => {
      const el = trackRef.current
      if (!el) return
      const target = Math.max(0, Math.min(count - 1, i))
      const child = el.children[target] as HTMLElement | undefined
      if (!child) return
      const pl = parseFloat(getComputedStyle(el).paddingLeft) || 0
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      el.scrollTo({ left: child.offsetLeft - pl, behavior: reduce ? 'auto' : 'smooth' })
      setIndex(target)
    },
    [count],
  )

  return { trackRef, index, goTo }
}
